import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Button, makeStyles, Snackbar, TextField, Typography } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import deepEqual from 'deep-equal'
import lodash from 'lodash'

import actions from '../../../redux/actions'
import config from '../../../config'
import { UPDATE_SECURITY_ROLE_USERS } from '../../../redux/data/types'
import RestrictedComponent from '../../../lib/components/form/RestrictedComponent'
import schema from '../../../routes/permissions/schema'

const useStyles = makeStyles(theme => ({
  selectorActions: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      marginLeft: '1rem'
    }
  },
  selectorContainer: {
    padding: '1rem',
    '& > *': {
      marginTop: '1rem'
    }
  }
}))

const UserRoleSelector = props => {
  const { users, role, thisUser, onClose, assignUsers, editing, requestPending, requestError } = props
  const [assignedUsers, setAssignedUsers] = useState(role && role.assignedUsers || [])
  const [showSnackbar, setShowSnackbar] = useState(false)
  const classes = useStyles()

  let snackMessage = requestPending ? 'In Progress' : 'Success'
  if (requestError) {
    snackMessage = 'Error: ' + requestError
  }
  useEffect(() => {
    if (requestPending || requestError) {
      setShowSnackbar(true)
    }
  }, [requestError, requestPending])

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
  }

  const handleSelectUser = selected => {
    setAssignedUsers(selected)
  }

  const handleAssignUsers = () => {
    // console.log('would send request with:', role.roleID, assignedUsers.map(user => user.userId))
    const roleID = role && role.roleID
    const userIDS = assignedUsers.map(user => user.userId)
    assignUsers({ roleID, userIDS })
  }


  const userOptions = users.filter(user => {
    return thisUser.userId !== user.userId &&
      !assignedUsers.find(assignedUser => assignedUser.userId === user.userId)
  })

  // remove the users already assigned to the role from the selector
  // this avoids deleting a users role; a user must always have a role
  const selected = assignedUsers.filter(assignedUser => {
    return !role || editing || !role.assignedUsers.find(user => user.userId === assignedUser.userId)
  })

  return <>
    <div className={classes.selectorContainer}>
      <Typography variant="h6">
        Assign <b>{role && role.roleName}</b> role to users
      </Typography>
      {role && role.roleID ? <Alert severity="info">
        Users must be assigned a role. Users previously assigned to this role cannot be removed without be added to a different role.
      </Alert> : null}
      <Autocomplete
        onChange={(ev, val) => {
          // merge selected users and role's previous users
          if (role && role.roleID) {
            handleSelectUser(lodash.uniqBy([...val, ...role.assignedUsers], user => user.userId))
          } else {
            handleSelectUser([...val])
          }
        }}
        disableCloseOnSelect
        autoComplete
        autoHighlight
        multiple
        options={userOptions}
        getOptionLabel={user => user.firstName + ' ' + user.lastName}
        filterSelectedOptions
        value={selected}
        renderInput={(params) => {
          return <TextField
            {...params}
            variant="outlined"
            placeholder="Users"
          />
        }}
      />
      <div className={classes.selectorActions}>
        <Button
          color="secondary"
          variant="contained"
          onClick={onClose}
        >
          Close
      </Button>
        <RestrictedComponent permissionCheck={schema.ACCOUNT.ANY_UPDATE}>
          <Button
            disabled={
              (role && deepEqual(assignedUsers, role.assignedUsers)) ||
              assignedUsers.length === 0
            }
            variant="contained"
            color="primary"
            onClick={handleAssignUsers}>
            Assign
      </Button>
        </RestrictedComponent>
      </div>
    </div>
    <Snackbar open={showSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
      <Alert onClose={handleSnackbarClose} severity={requestError ? 'error' : (requestPending ? 'info' : 'success')}>
        {snackMessage}
      </Alert>
    </Snackbar>
  </>
}

const mergeProps = (stateProps, _dispatchProps, ownProps) => {
  const dispatch = func => _dispatchProps.callDispatch(func)
  const assignUsers = ({ roleID, userIDS }) => {
    // can update user security roles with a group of assigned userIDS
    dispatch(actions.request.sendRequest({
      type: UPDATE_SECURITY_ROLE_USERS,
      url: `${config.uris.crud}SecurityRoles/updateSecurityRole`,
      postData: {
        clientID: stateProps.clientID,
        roleID,
        userIDS
      }
    }))
  }
  // assignUsers can come from own props so these are merged this way
  return { assignUsers, ...stateProps, ...ownProps }
}

const mapStateToProps = state => {
  const thisUser = state.data.user
  const clientID = thisUser.clientId
  const users = state.data.users
  const requestPending = state.request[UPDATE_SECURITY_ROLE_USERS] && state.request[UPDATE_SECURITY_ROLE_USERS].pending
  const requestError = requestPending === false && state.request[UPDATE_SECURITY_ROLE_USERS]?.error?.message
  return { clientID, thisUser, users, requestPending, requestError }
}

const mapDispatchToProps = dispatch => ({
  callDispatch: func => dispatch(func),
})

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(UserRoleSelector)
